import { assertFmEvidenceDescriptor } from '../FmEvidence.validation';
import { CHART_CHROME_KEYS, validateChart, validateChartChrome } from './chartFields.validation';
import {
  IMAGE_MEDIA_KEYS,
  MAP_MEDIA_KEYS,
  validateActionSet,
  validateMedia,
} from './mediaFields.validation';
import {
  assertArray,
  assertNonEmptyString,
  assertRecord,
  hasOwn,
  inspectNested,
  rejectUnknownKeys,
  validateAction,
  validateBadges,
  validateChapters,
  validateContextControl,
  validateEnum,
  validateOptionalFiniteNumber,
  validateOptionalString,
  validateRankedRows,
  validateStringArray,
  validateTimelineEvents,
  validateValueExplanations,
} from './elementFields.validation';
import {
  validateComparisonMatrix,
  validateRankedTableInsight,
  validateTable,
  validateTableCellValue,
} from './tableFields.validation';
import { validateCorridorTrajectory } from './corridorFields.validation';
import { validateMapSelection } from './mapSelection.validation';
import { validateMapPresentationContext } from './mapPresentation.validation';
import { DOCUMENT_ELEMENT_KEYS, validateDocumentElement } from './documentFields.validation';
import {
  FM_ELEMENT_SCALES,
  FM_ELEMENT_TYPES,
  type FmElement,
  type FmElementType,
} from './elements.types';

type ElementRecord = Record<string, unknown>;
type ElementValidator = (element: ElementRecord, path: string) => void;

const ELEMENT_KEYS: Record<FmElementType, readonly string[]> = {
  copy: ['eyebrow', 'heading', 'lede', 'body', 'list', 'sourceNote', 'badges', 'scale'],
  keyFigures: ['figures', 'scale'],
  facts: ['items'],
  definitionList: ['items'],
  chart: ['chart', 'corridor', 'unavailableReason', ...CHART_CHROME_KEYS],
  map: [...MAP_MEDIA_KEYS, 'selection'],
  image: IMAGE_MEDIA_KEYS,
  document: DOCUMENT_ELEMENT_KEYS,
  table: ['title', 'table', 'rankedTable', 'comparison', 'unavailableReason', 'disclosure'],
  rankedList: ['title', 'rows', 'valueHeader', 'emptyLabel', 'limit', 'highlightTop'],
  steps: ['items'],
  timeline: ['events'],
  notice: ['tone', 'title', 'description'],
  actions: ['primary', 'secondary', 'set'],
  provenance: ['evidence'],
  lens: ['control'],
};

function invalid(path: string, message: string): never {
  throw new Error(`Invalid descriptor: ${path} ${message}.`);
}

function assertNonEmptyArray(value: unknown, path: string): asserts value is unknown[] {
  assertArray(value, path);
  if (value.length === 0) invalid(path, 'must not be empty');
}

function requireExactlyOne(element: ElementRecord, keys: readonly string[], path: string): string {
  const present = keys.filter((key) => hasOwn(element, key));
  if (present.length !== 1) invalid(path, `requires exactly one of ${keys.join(', ')}`);
  return present[0];
}

/** The media-slot fields run through the shared validator; multi-select mode is the element's own. */
const validateMapElement: ElementValidator = (element, path) => {
  const { selection, ...media } = element;
  validateMedia(asMediaSlot(media, 'map'), path);
  if (selection !== undefined) validateMapSelection(selection, element.figure, `${path}.selection`);
  const figure = element.figure as { slotProps?: { presentation?: unknown } } | undefined;
  validateMapPresentationContext(
    figure?.slotProps?.presentation,
    selection !== undefined,
    `${path}.figure.slotProps.presentation`,
  );
};

/** Removes the element discriminator so the shared media-slot validator can run unchanged. */
function asMediaSlot(element: ElementRecord, kind: 'map' | 'image'): ElementRecord {
  const { element: _element, testId: _testId, ...slot } = element;
  return { ...slot, kind };
}

const validateCopy: ElementValidator = (element, path) => {
  const contentKeys = ELEMENT_KEYS.copy.filter((key) => key !== 'scale');
  validateEnum(element.scale, FM_ELEMENT_SCALES, `${path}.scale`, true);
  if (element.scale === 'display' && !hasOwn(element, 'heading')) {
    invalid(`${path}.scale`, 'is "display" but the copy has no heading to set at the display tier');
  }
  if (!contentKeys.some((key) => hasOwn(element, key))) {
    invalid(path, `requires at least one of ${contentKeys.join(', ')}`);
  }
  ['eyebrow', 'heading', 'lede', 'sourceNote'].forEach((key) =>
    validateOptionalString(element, key, path),
  );
  ['body', 'list'].forEach((key) => {
    if (!hasOwn(element, key)) return;
    assertNonEmptyArray(element[key], `${path}.${key}`);
    validateStringArray(element[key], `${path}.${key}`);
  });
  if (hasOwn(element, 'badges')) validateBadges(element.badges, `${path}.badges`);
};

const KEY_FIGURE_STATUSES = ['good', 'caution', 'loss'] as const;
const DELTA_DIRECTIONS = ['up', 'down', 'flat'] as const;

/**
 * A key figure is a formatted number: it carries a digit and no letters, so a
 * word ("Mänty", "Kyllä") or a unit typed into the value ("41.6 ha") is refused
 * by name; words are facts and the unit has its own slot.
 */
function assertNumericFigureValue(value: string, path: string): void {
  if (!/\d/.test(value) || /\p{L}/u.test(value)) {
    invalid(
      path,
      `must be a formatted number without words or units ("${value}"); put text in a facts element and the unit in unit`,
    );
  }
}

/** Units print as typeset symbols; an ASCII exponent is a formatter that was skipped. */
function assertTypesetUnit(unit: unknown, path: string): void {
  if (typeof unit === 'string' && /m[23](\b|\/|$)/.test(unit)) {
    invalid(path, `must use typeset exponents (m², m³), not "${unit}"`);
  }
}

function validateKeyFigureDelta(value: unknown, path: string): void {
  assertRecord(value, path);
  rejectUnknownKeys(value, ['label', 'direction'], path);
  assertNonEmptyString(value.label, `${path}.label`);
  validateEnum(value.direction, DELTA_DIRECTIONS, `${path}.direction`);
}

function validateKeyFigure(value: unknown, path: string): void {
  assertRecord(value, path);
  rejectUnknownKeys(
    value,
    ['label', 'value', 'unit', 'footnote', 'status', 'delta', 'unavailableReason'],
    path,
  );
  assertNonEmptyString(value.label, `${path}.label`);
  ['unit', 'footnote'].forEach((key) => validateOptionalString(value, key, path));
  assertTypesetUnit(value.unit, `${path}.unit`);
  if (hasOwn(value, 'status')) validateEnum(value.status, KEY_FIGURE_STATUSES, `${path}.status`);
  if (hasOwn(value, 'delta')) validateKeyFigureDelta(value.delta, `${path}.delta`);
  if (!hasOwn(value, 'value')) invalid(path, 'requires value (null when unavailable)');
  if (value.value === null) {
    if (!hasOwn(value, 'unavailableReason')) {
      invalid(`${path}.unavailableReason`, 'is required when value is null');
    }
    assertNonEmptyString(value.unavailableReason, `${path}.unavailableReason`);
    if (hasOwn(value, 'delta')) invalid(`${path}.delta`, 'is not allowed when value is null');
    return;
  }
  assertNonEmptyString(value.value, `${path}.value`);
  assertNumericFigureValue(value.value, `${path}.value`);
  if (hasOwn(value, 'unavailableReason')) {
    invalid(`${path}.unavailableReason`, 'is only allowed when value is null');
  }
}

const validateKeyFigures: ElementValidator = (element, path) => {
  validateEnum(element.scale, FM_ELEMENT_SCALES, `${path}.scale`, true);
  assertNonEmptyArray(element.figures, `${path}.figures`);
  if (element.scale === 'display' && element.figures.length > 4) {
    invalid(`${path}.figures`, 'holds more than four hero numbers; a stat band carries one to four');
  }
  element.figures.forEach((figure, index) =>
    validateKeyFigure(figure, `${path}.figures[${index}]`),
  );
};

const validateFacts: ElementValidator = (element, path) => {
  assertNonEmptyArray(element.items, `${path}.items`);
  validateValueExplanations(element.items, `${path}.items`);
};

/** Terms are unique and every definition says something; a glossary never repeats a term. */
const validateDefinitionList: ElementValidator = (element, path) => {
  assertNonEmptyArray(element.items, `${path}.items`);
  const terms = new Set<string>();
  element.items.forEach((item, index) => {
    const itemPath = `${path}.items[${index}]`;
    assertRecord(item, itemPath);
    rejectUnknownKeys(item, ['term', 'definition'], itemPath);
    assertNonEmptyString(item.term, `${itemPath}.term`);
    assertNonEmptyString(item.definition, `${itemPath}.definition`);
    if (terms.has(item.term)) invalid(`${itemPath}.term`, `repeats the term "${item.term}"`);
    terms.add(item.term);
  });
};

const validateChartElement: ElementValidator = (element, path) => {
  const data = requireExactlyOne(element, ['chart', 'corridor', 'unavailableReason'], path);
  if (data === 'corridor') {
    rejectUnknownKeys(element, ['element', 'testId', 'corridor'], path);
  } else {
    validateChartChrome(element, path);
  }
  if (data === 'unavailableReason') {
    assertNonEmptyString(element.unavailableReason, `${path}.unavailableReason`);
  } else if (data === 'corridor') {
    validateCorridorTrajectory(element.corridor, `${path}.corridor`);
  } else {
    validateChart(element.chart, `${path}.chart`);
  }
};

const PREVIEW_KEYS = ['previewLimit', 'showAllLabel', 'showLessLabel'];

/** Preview lives once, in `disclosure.preview`, never as table-local fields. */
function rejectTablePreviewKeys(table: unknown, path: string): void {
  assertRecord(table, path);
  const key = PREVIEW_KEYS.find((candidate) => hasOwn(table, candidate));
  if (key) invalid(`${path}.${key}`, 'moves to disclosure.preview');
}

/** Runs after `validateTable`, so columns are records with a non-empty `key`. */
function tableColumnKeys(table: ElementRecord): Set<string> {
  return new Set((table.columns as readonly ElementRecord[]).map((column) => String(column.key)));
}

/** A plain table row is identified by `key`, a ranked-table row by `id`. */
function rowIdentity(row: unknown): unknown {
  const record = row as ElementRecord;
  return hasOwn(record, 'key') ? record.key : record.id;
}

function validatePreview(value: unknown, rows: readonly unknown[], path: string): void {
  assertRecord(value, path);
  rejectUnknownKeys(value, ['limit', 'showAllLabel', 'showLessLabel', 'revealRowKey'], path);
  if (typeof value.limit !== 'number' || !Number.isInteger(value.limit) || value.limit < 1) {
    invalid(`${path}.limit`, 'must be a positive integer');
  }
  ['showAllLabel', 'showLessLabel'].forEach((key) =>
    assertNonEmptyString(value[key], `${path}.${key}`),
  );
  validateOptionalString(value, 'revealRowKey', path);
  if (
    hasOwn(value, 'revealRowKey') &&
    !rows.some((row) => rowIdentity(row) === value.revealRowKey)
  ) {
    invalid(`${path}.revealRowKey`, 'must reference a served row');
  }
}

function validateSelectOptions(value: unknown, path: string): void {
  assertNonEmptyArray(value, path);
  const seen = new Set<unknown>();
  value.forEach((option, index) => {
    const optionPath = `${path}[${index}]`;
    assertRecord(option, optionPath);
    rejectUnknownKeys(option, ['value', 'label', 'disabled'], optionPath);
    ['value', 'label'].forEach((key) => assertNonEmptyString(option[key], `${optionPath}.${key}`));
    if (hasOwn(option, 'disabled') && typeof option.disabled !== 'boolean') {
      invalid(`${optionPath}.disabled`, 'must be a boolean');
    }
    if (seen.has(option.value)) invalid(`${optionPath}.value`, 'duplicates another option');
    seen.add(option.value);
  });
}

function validateSelectColumn(
  value: unknown,
  columns: Set<string>,
  rowKeys: ReadonlySet<string>,
  path: string,
): void {
  assertRecord(value, path);
  rejectUnknownKeys(value, ['columnKey', 'cells', 'onChange'], path);
  assertNonEmptyString(value.columnKey, `${path}.columnKey`);
  if (!columns.has(value.columnKey)) invalid(`${path}.columnKey`, 'must name a table column');
  if (typeof value.onChange !== 'function') invalid(`${path}.onChange`, 'must be a function');
  assertRecord(value.cells, `${path}.cells`);
  Object.entries(value.cells).forEach(([rowKey, cell]) => {
    const cellPath = `${path}.cells.${rowKey}`;
    if (!rowKeys.has(rowKey)) invalid(cellPath, 'must name a served row');
    assertRecord(cell, cellPath);
    rejectUnknownKeys(cell, ['filterId', 'ariaLabel', 'options', 'disabled'], cellPath);
    assertNonEmptyString(cell.filterId, `${cellPath}.filterId`);
    assertNonEmptyString(cell.ariaLabel, `${cellPath}.ariaLabel`);
    if (hasOwn(cell, 'disabled') && typeof cell.disabled !== 'boolean') {
      invalid(`${cellPath}.disabled`, 'must be a boolean');
    }
    validateSelectOptions(cell.options, `${cellPath}.options`);
  });
}

function validateRowDetails(value: unknown, rowKeys: ReadonlySet<string>, path: string): void {
  assertRecord(value, path);
  Object.entries(value).forEach(([rowKey, details]) => {
    const detailsPath = `${path}.${rowKey}`;
    if (!rowKeys.has(rowKey)) invalid(detailsPath, 'must name a served row');
    assertNonEmptyArray(details, detailsPath);
    const ids = new Set<unknown>();
    details.forEach((detail, index) => {
      const detailPath = `${detailsPath}[${index}]`;
      assertRecord(detail, detailPath);
      rejectUnknownKeys(detail, ['id', 'label', 'value', 'detail'], detailPath);
      assertNonEmptyString(detail.id, `${detailPath}.id`);
      assertNonEmptyString(detail.label, `${detailPath}.label`);
      validateTableCellValue(detail.value, `${detailPath}.value`);
      validateOptionalString(detail, 'detail', detailPath);
      if (ids.has(detail.id)) invalid(`${detailPath}.id`, 'duplicates another detail');
      ids.add(detail.id);
    });
  });
}

function validateDisclosure(element: ElementRecord, data: string, path: string): void {
  if (!hasOwn(element, 'disclosure')) return;
  const disclosure = element.disclosure;
  assertRecord(disclosure, path);
  if (data !== 'table' && data !== 'rankedTable') {
    invalid(path, 'applies to a table or ranked table only');
  }
  const allowed =
    data === 'table' ? ['preview', 'groupByField', 'expandableRows', 'selectColumns'] : ['preview'];
  rejectUnknownKeys(disclosure, allowed, path);
  const source = element[data] as ElementRecord;
  assertArray(source.rows, `${path}.rows`);
  const rows = source.rows;
  if (hasOwn(disclosure, 'preview')) validatePreview(disclosure.preview, rows, `${path}.preview`);
  if (hasOwn(disclosure, 'groupByField')) {
    assertNonEmptyString(disclosure.groupByField, `${path}.groupByField`);
    const field = disclosure.groupByField;
    rows.forEach((row, index) =>
      assertNonEmptyString((row as ElementRecord)[field], `${path}.groupByField on rows[${index}]`),
    );
  }
  const columns = data === 'table' ? tableColumnKeys(source) : new Set<string>();
  const rowKeys = new Set(rows.map((row) => String(rowIdentity(row))));
  if (hasOwn(disclosure, 'expandableRows')) {
    validateRowDetails(disclosure.expandableRows, rowKeys, `${path}.expandableRows`);
  }
  if (hasOwn(disclosure, 'selectColumns')) {
    assertNonEmptyArray(disclosure.selectColumns, `${path}.selectColumns`);
    disclosure.selectColumns.forEach((column, index) =>
      validateSelectColumn(column, columns, rowKeys, `${path}.selectColumns[${index}]`),
    );
  }
}

const validateTableElement: ElementValidator = (element, path) => {
  validateOptionalString(element, 'title', path);
  const data = requireExactlyOne(
    element,
    ['table', 'rankedTable', 'comparison', 'unavailableReason'],
    path,
  );
  if (data === 'table') {
    rejectTablePreviewKeys(element.table, `${path}.table`);
    validateTable(element.table, `${path}.table`);
  }
  if (data === 'rankedTable') {
    rejectTablePreviewKeys(element.rankedTable, `${path}.rankedTable`);
    validateRankedTableInsight(element.rankedTable, `${path}.rankedTable`);
  }
  if (data === 'comparison') validateComparisonMatrix(element.comparison, `${path}.comparison`);
  if (data === 'unavailableReason') {
    assertNonEmptyString(element.unavailableReason, `${path}.unavailableReason`);
  }
  validateDisclosure(element, data, `${path}.disclosure`);
};

const validateRankedList: ElementValidator = (element, path) => {
  validateRankedRows(element.rows, `${path}.rows`);
  ['title', 'valueHeader', 'emptyLabel'].forEach((key) =>
    validateOptionalString(element, key, path),
  );
  ['limit', 'highlightTop'].forEach((key) => validateOptionalFiniteNumber(element, key, path));
  if (Array.isArray(element.rows) && element.rows.length === 0 && !hasOwn(element, 'emptyLabel')) {
    invalid(`${path}.emptyLabel`, 'is required when rows is empty; an empty list names why');
  }
};

const validateNotice: ElementValidator = (element, path) => {
  validateEnum(element.tone, ['info', 'warning', 'error', 'success'], `${path}.tone`);
  assertNonEmptyString(element.title, `${path}.title`);
  assertNonEmptyString(element.description, `${path}.description`);
};

const validateActions: ElementValidator = (element, path) => {
  if (hasOwn(element, 'set')) {
    if (hasOwn(element, 'primary') || hasOwn(element, 'secondary')) {
      invalid(path, 'carries either an action set or primary/secondary actions, not both');
    }
    validateActionSet(element.set, `${path}.set`);
    return;
  }
  if (!hasOwn(element, 'primary') && !hasOwn(element, 'secondary')) {
    invalid(path, 'requires primary, secondary or set');
  }
  if (hasOwn(element, 'primary')) validateAction(element.primary, `${path}.primary`);
  if (hasOwn(element, 'secondary')) {
    if (!hasOwn(element, 'primary')) assertNonEmptyArray(element.secondary, `${path}.secondary`);
    assertArray(element.secondary, `${path}.secondary`);
    element.secondary.forEach((action, index) =>
      validateAction(action, `${path}.secondary[${index}]`),
    );
  }
};

/** The evidence validator names paths from its own `evidence` root; re-anchor them at the element. */
const validateProvenance: ElementValidator = (element, path) => {
  try {
    assertFmEvidenceDescriptor(element.evidence);
  } catch (error) {
    if (!(error instanceof Error)) throw error;
    throw new Error(
      error.message.replace(
        'Invalid evidence descriptor: evidence',
        `Invalid descriptor: ${path}.evidence`,
      ),
    );
  }
};

const ELEMENT_VALIDATORS: Record<FmElementType, ElementValidator> = {
  copy: validateCopy,
  keyFigures: validateKeyFigures,
  definitionList: validateDefinitionList,
  facts: validateFacts,
  chart: validateChartElement,
  map: validateMapElement,
  image: (element, path) => validateMedia(asMediaSlot(element, 'image'), path),
  document: validateDocumentElement,
  table: validateTableElement,
  rankedList: validateRankedList,
  steps: (element, path) => {
    assertNonEmptyArray(element.items, `${path}.items`);
    validateChapters(element.items, `${path}.items`);
  },
  timeline: (element, path) => {
    assertNonEmptyArray(element.events, `${path}.events`);
    validateTimelineEvents(element.events, `${path}.events`);
  },
  notice: validateNotice,
  actions: validateActions,
  provenance: validateProvenance,
  lens: (element, path) => validateContextControl(element.control, `${path}.control`),
};

function isElementType(value: unknown): value is FmElementType {
  return typeof value === 'string' && (FM_ELEMENT_TYPES as readonly string[]).includes(value);
}

/**
 * Validates one element at `path`. The structural guard runs first and treats
 * the element root as nested content, so an element dressed as a descriptor
 * (`kind` + `id` + `title`) fails as a nested descriptor rather than as an
 * unknown key.
 */
export function validateElement(value: unknown, path = 'element'): asserts value is FmElement {
  assertRecord(value, path);
  inspectNested(value, path);
  if (!isElementType(value.element)) {
    invalid(`${path}.element`, `must be one of ${FM_ELEMENT_TYPES.join(', ')}`);
  }
  rejectUnknownKeys(value, ['element', 'testId', ...ELEMENT_KEYS[value.element]], path);
  validateOptionalString(value, 'testId', path);
  ELEMENT_VALIDATORS[value.element](value, path);
}
